import type { paths } from '@/types/api-extensions'
import type { AxiosRequestConfig } from 'axios'

import axios from 'axios'

const instance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CMS_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Strip the leading slash from all request paths so that axios resolves them
// relative to the full baseURL path (e.g. /cms/api/) rather than treating them
// as absolute paths from the origin. Without this, api.get('/abouts') resolves
// to https://host/abouts instead of https://host/cms/api/abouts → 500.
instance.interceptors.request.use(config => {
  if (config.url?.startsWith('/')) {
    config.url = config.url.slice(1)
  }
  return config
})

// NOTE: `http` always points to the production origin regardless of environment.
// Use it only for endpoints that have no CMS equivalent (e.g. production-only proxies).
// Never use it in features that need to work on UAT.
export const http = axios.create({ baseURL: 'https://www.alrajhibank.com.my' })

export const isCancel = axios.isCancel

// ─── Response / body type helpers ─────────────────────────────────────────────

type GetResponse<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer R } } } }
}
  ? R
  : never

// ─── Spec-derived response helpers (for typing component state from the CMS) ───

/** Full JSON body of a GET 200 for `P` — the same shape `api.get(P)` resolves to. */
export type CmsResponse<P extends keyof paths> = NonNullable<GetResponse<P>>

/** The `data` payload of a GET 200 — the array for collections, the object for single types. */
export type CmsData<P extends keyof paths> =
  CmsResponse<P> extends { data?: infer D } ? NonNullable<D> : never

/**
 * A single CMS item from a GET 200 — the element type for collections, or the
 * object itself for single types. Use it to type state instead of `any`/`never`:
 *
 *   const [promos, setPromos] = useState<CmsItem<'/promotion-and-campaigns'>[]>([])
 */
export type CmsItem<P extends keyof paths> =
  CmsData<P> extends readonly (infer Item)[] ? Item : CmsData<P>

type PostBody<P extends keyof paths> = paths[P] extends {
  post: { requestBody: { content: { 'application/json': infer B } } }
}
  ? B
  : never

type PostResponse<P extends keyof paths> = paths[P] extends {
  post: { responses: { 200: { content: { 'application/json': infer R } } } }
}
  ? R
  : never

// ─── Typed api object ──────────────────────────────────────────────────────────

/**
 * Drop-in replacement for axios with types inferred from the OpenAPI spec.
 *
 * @example
 * import api from '@/utils/apiClient'
 *
 * // GET — response body is fully typed
 * const { data } = await api.get('/abouts', { params: { pLevel: '' } })
 *
 * // POST — request body shape is enforced by the spec
 * const { data } = await api.post('/enquiry-forms', { data: formData })
 */
const api = {
  get<P extends keyof paths>(path: P | `${P}?${string}`, config?: AxiosRequestConfig) {
    return instance.get<GetResponse<P>>(path as string, config)
  },

  post<P extends keyof paths>(path: P, body: PostBody<P>, config?: AxiosRequestConfig) {
    return instance.post<PostResponse<P>>(path as string, body, config)
  },

  put<P extends keyof paths>(path: P, body: unknown, config?: AxiosRequestConfig) {
    return instance.put(path as string, body, config)
  },

  delete<P extends keyof paths>(path: P, config?: AxiosRequestConfig) {
    return instance.delete(path as string, config)
  },

  /** Underlying axios instance — use for interceptors, defaults, etc. */
  instance
}

export default api
